// Frontend telemetry SDK. Captures client errors — React render crashes
// (via the error boundary), uncaught `window.onerror`, unhandled promise
// rejections, and failed API calls — plus a rolling breadcrumb trail, and
// batches them to `POST /telemetry` (session-authed) which forwards to the
// unified log store (the PostgreSQL `app_logs` table).
//
// Correlation: each API request mints a `trace_id` (sent as `X-Request-Id`);
// if that request fails, the frontend error line carries the SAME trace_id as
// the API's own 5xx line, so one bug is one trace across both tiers.
//
// PII: every free-text field is redacted here before it leaves the browser
// (the server re-redacts as defense-in-depth). Never send raw PII.

import { API_BASE } from '@/api/base';
import { readCsrf } from '@/api/csrf';

const ENDPOINT = `${API_BASE}/telemetry`;

type Level = 'debug' | 'info' | 'warn' | 'error';

interface LogLine {
  ts: string;
  level: Level;
  source: 'frontend';
  message: string;
  route?: string | null;
  http_status?: number | null;
  error_kind?: string | null;
  stack_stub?: string | null;
  trace_id?: string | null;
  context_json?: string | null;
  release?: string | null;
  env?: string | null;
}

interface Breadcrumb {
  t: string;
  kind: string;
  msg: string;
}

const MAX_QUEUE = 100;
const MAX_BREADCRUMBS = 20;
const FLUSH_DELAY_MS = 4000;
const ERROR_FLUSH_DELAY_MS = 800;
// How often buffered lines look for a session again while there is none (a
// guest, or after a 401). Reading a cookie is all it costs, and it stops when
// the queue is empty.
const SESSION_POLL_MS = 5000;

const queue: LogLine[] = [];
const breadcrumbs: Breadcrumb[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let started = false;

// ── PII redaction (mirror of shared::redact) ─────────────────────────────────
//
// This is a mirror, and `shared/testdata/redaction/cases.json` is what keeps it
// one: the same fixture runs here (telemetry.redaction-parity.test.ts) and in
// Rust (shared/tests/redaction_parity.rs). It used to be a loose approximation
// and had drifted badly — nine sensitive keys against Rust's twenty-nine, so
// `access_token=`, `cookie=`, `session=` and the `x-amz-*` signing params all
// left the browser in the clear, and a character-count threshold that redacted
// six-digit runs Rust kept.
//
// The server re-redacts everything, so the browser copy is defense in depth
// rather than the guarantee. It still has to agree, because a line the browser
// mangles is mangled for good.

// Mirrors `KEYS` in shared/src/redact.rs. Order is irrelevant; membership is not.
const SENSITIVE_KEYS = [
  'email', 'phone', 'token', 'access_token', 'refresh_token', 'password', 'passwd',
  'secret', 'client_secret', 'authorization', 'api_key', 'cookie', 'set_cookie',
  'csrf', 'session', 'session_id', 'internal_token', 'bearer', 'appsecret_proof',
  'signature', 'x-amz-signature', 'x-amz-credential', 'x-amz-security-token',
  'ttd-auth', 'request_body', 'response_body', 'body', 'payload', 'headers',
];

// Mirrors `SENSITIVE` in shared/src/redact.rs::has_sensitive_prefix.
const SENSITIVE_PREFIXES = ['phone', 'tel', 'mobile', 'fax', 'ssn', 'card', 'pan', 'acct'];

const isAlnum = (c: string) => /[A-Za-z0-9]/.test(c);
const isIdentifierChar = (c: string) => /[A-Za-z_]/.test(c);

// Mirrors `ends_a_value` in shared/src/redact.rs.
const VALUE_DELIMITERS = '& "\',}\n';

// Mirrors `AUTH_SCHEMES`. The scheme word is not the secret — the token one
// space behind it is, so a skip that stops at the first space leaves the whole
// bearer token in the line.
const AUTH_SCHEMES = ['bearer', 'basic', 'digest', 'token'];

/** End of the unquoted run starting at `from`. Mirrors `skip_value_token`. */
function skipValueToken(s: string, from: number): number {
  let j = from;
  while (j < s.length && !VALUE_DELIMITERS.includes(s[j]!)) j += 1;
  return j;
}

/** Index just past the value replaced with `[redacted]`. Mirrors
 *  `skip_redacted_value` — the reasoning lives there. The skip used to start
 *  *at* the delimiter set, so `password="hunter2"` and
 *  `Authorization: Bearer eyJ…` consumed nothing and the browser re-emitted the
 *  secret right behind the marker. */
function skipRedactedValue(s: string, from: number, headerForm: boolean): number {
  let j = from;
  if (headerForm && j < s.length && s[j] === ' ') j += 1;
  if (j < s.length && (s[j] === '"' || s[j] === "'")) {
    const quote = s[j];
    j += 1;
    while (j < s.length && s[j] !== quote && s[j] !== '\n') j += 1;
    if (j < s.length && s[j] === quote) j += 1;
    return j;
  }
  let tokenStart = j;
  let end = skipValueToken(s, tokenStart);
  if (
    headerForm
    && AUTH_SCHEMES.includes(s.slice(tokenStart, end).toLowerCase())
    && end < s.length
    && s[end] === ' '
  ) {
    tokenStart = end + 1;
    end = skipValueToken(s, tokenStart);
  }
  return end;
}

function redactSensitiveParams(s: string): string {
  const lower = s.toLowerCase();
  let out = '';
  let i = 0;
  outer: while (i < s.length) {
    for (const key of SENSITIVE_KEYS) {
      const end = i + key.length;
      if (
        end < s.length
        && lower.startsWith(key, i)
        && (s[end] === '=' || s[end] === ':')
        && (i === 0 || !isAlnum(s[i - 1]!))
      ) {
        out += s.slice(i, end + 1) + '[redacted]';
        i = skipRedactedValue(s, end + 1, s[end] === ':');
        continue outer;
      }
    }
    out += s[i];
    i += 1;
  }
  return out;
}

/** Mirrors `CREDENTIAL_PREFIXES`: SMART's API keys and OAuth tokens. */
const CREDENTIAL_PREFIXES = ['sam_pat_', 'sam_at_', 'sam_rt_'];

/** Mirrors `redact_credential_tokens`: keep the prefix, drop 8+ base64url characters after it. */
function redactCredentialTokens(s: string): string {
  if (!s.includes('sam_')) return s;
  let out = '';
  let i = 0;
  outer: while (i < s.length) {
    for (const prefix of CREDENTIAL_PREFIXES) {
      if (s.startsWith(prefix, i)) {
        const start = i + prefix.length;
        let end = start;
        while (end < s.length && /[A-Za-z0-9_-]/.test(s[end]!)) end += 1;
        if (end - start >= 8) {
          out += `${prefix}[redacted-token]`;
          i = end;
          continue outer;
        }
      }
    }
    out += s[i];
    i += 1;
  }
  return out;
}

function redactHexTokens(s: string): string {
  let out = '';
  let i = 0;
  while (i < s.length) {
    if (isAlnum(s[i]!)) {
      const start = i;
      while (i < s.length && isAlnum(s[i]!)) i += 1;
      const token = s.slice(start, i);
      out += token.length === 64 && /^[0-9a-fA-F]+$/.test(token) ? '[redacted-token]' : token;
    } else {
      out += s[i];
      i += 1;
    }
  }
  return out;
}

function hasSensitivePrefix(beforeRun: string): boolean {
  const word = /[A-Za-z0-9_]*$/.exec(beforeRun)?.[0] ?? '';
  const tail = word.split('_').filter(Boolean).pop() ?? word;
  return SENSITIVE_PREFIXES.includes(tail.toLowerCase());
}

function isCalendarRun(run: string): boolean {
  const groups = run.split(' ').filter(Boolean);
  return run.includes('-') && groups.every((g) => /^\d{4}-\d{2}-\d{2}$/.test(g));
}

// A provider's machine error code is kept: on 2026-10-02 three Meta publishes
// read as `error_subcode=[redacted-num]`, and the one number naming the cause
// (1885396, a refused call to action) was readable nowhere an operator looked.
// Mirrors ERROR_CODE_KEYS / is_error_code_run / follows_error_code_key in
// shared::redact, exactly: an unbroken run of at most nine digits, right after
// `<key>=` at a word boundary or after the quoted `"<key>":` (one optional
// space), where the quoted form also takes exactly `"code"`.
const ERROR_CODE_KEYS = ['error_code', 'error_subcode', 'provider_code', 'provider_subcode'];
const MAX_ERROR_CODE_DIGITS = 9;

function isErrorCodeRun(beforeRun: string, run: string): boolean {
  return run.length <= MAX_ERROR_CODE_DIGITS
    && /^[0-9]+$/.test(run)
    && followsErrorCodeKey(beforeRun);
}

function followsErrorCodeKey(before: string): boolean {
  if (before.endsWith('=')) {
    const head = before.slice(0, -1);
    return ERROR_CODE_KEYS.some((key) => head.endsWith(key)
      && !/[A-Za-z0-9_]$/.test(head.slice(0, head.length - key.length)));
  }
  let head = before.endsWith(' ') ? before.slice(0, -1) : before;
  if (!head.endsWith('":')) return false;
  head = head.slice(0, -2);
  return [...ERROR_CODE_KEYS, 'code'].some((key) => head.endsWith(key)
    && head.slice(0, head.length - key.length).endsWith('"'));
}

/** A digit run is redacted only when it stands alone as a number — a run welded
 *  to a letter or `_` belongs to an identifier. Mirrors redact_long_digits. */
function redactLongDigits(s: string): string {
  let out = '';
  let i = 0;
  while (i < s.length) {
    if (/[0-9]/.test(s[i]!)) {
      const start = i;
      let digits = 0;
      let lastDigitEnd = i;
      while (i < s.length && /[0-9\- ]/.test(s[i]!)) {
        if (/[0-9]/.test(s[i]!)) {
          digits += 1;
          lastDigitEnd = i + 1;
        }
        i += 1;
      }
      let left = start;
      while (left > 0 && s[left - 1] === '-') left -= 1;
      let right = lastDigitEnd;
      while (right < s.length && s[right] === '-') right += 1;
      const gluedLeft = left > 0 && isIdentifierChar(s[left - 1]!);
      const gluedRight = right < s.length && isIdentifierChar(s[right]!);
      const sensitive = gluedLeft && hasSensitivePrefix(s.slice(0, left));
      const run = s.slice(start, lastDigitEnd);
      const redact = digits >= 7
        && (sensitive || (!gluedLeft && !gluedRight))
        && !isCalendarRun(run)
        && !isErrorCodeRun(s.slice(0, start), run);
      out += redact ? `[redacted-num]${s.slice(lastDigitEnd, i)}` : s.slice(start, i);
    } else {
      out += s[i];
      i += 1;
    }
  }
  return out;
}

function redactEmails(s: string): string {
  return s.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[redacted-email]');
}

export function redact(input: unknown): string {
  if (input == null) return '';
  const s = String(input);
  if (s === '') return '';
  return redactLongDigits(redactHexTokens(redactSensitiveParams(redactEmails(redactCredentialTokens(s)))));
}

// ── describing a thrown thing ────────────────────────────────────────────────
//
// `reason?.message ?? String(reason)` is not enough, and prod proved it: three
// lines on /campaigns/new (2026-08-11 21:16-21:20) reached the log store with an
// empty message, no stack and no trace id. `??` does not catch the empty
// string, so both `new Error('')` and a rejected `''` logged nothing — and
// because the storage signature is computed over the message, all three
// collapsed into one unidentifiable group.
//
// Every rung below is a real shape. The floor is a type name, never ''.

const BLANK = new Set(['', 'null', 'undefined', '{}', '""', '[object Object]']);

const isBlank = (s: string) => BLANK.has(s.trim());

/** A never-empty type name for the thing that was thrown. */
export function typeTag(reason: unknown): string {
  if (reason === null) return 'null';
  if (typeof reason !== 'object') return typeof reason;
  try {
    return (reason as { constructor?: { name?: string } }).constructor?.name
      || Object.prototype.toString.call(reason).slice(8, -1);
  } catch {
    return 'object';
  }
}

/** First rung that yields something an operator can act on. */
export function describeError(reason: unknown, fallback?: string): string {
  const rungs: Array<() => string | null | undefined> = [
    () => (typeof (reason as { message?: unknown })?.message === 'string'
      ? (reason as { message: string }).message
      : null),
    () => {
      const name = (reason as { name?: unknown })?.name;
      return typeof name === 'string' && name ? `${name}: no message` : null;
    },
    () => {
      const ctor = typeTag(reason);
      return reason !== null && typeof reason === 'object' && ctor !== 'Object'
        ? `${ctor}: no message`
        : null;
    },
    () => { try { return String(reason); } catch { return null; } },
    () => { try { return JSON.stringify(reason)?.slice(0, 500); } catch { return null; } },
  ];
  // Every rung is guarded, not just the ones that obviously parse: a reason
  // can be any value, including one whose `.message` getter throws. This runs
  // inside error handlers, so it must never become the error.
  for (const rung of rungs) {
    let value: string | null | undefined;
    try {
      value = rung();
    } catch {
      continue;
    }
    if (typeof value === 'string' && !isBlank(value)) return value;
  }
  return fallback ?? 'Unknown error';
}

/** A non-empty stack, or null — never the empty string. */
export function stackOf(reason: unknown): string | null {
  try {
    const stack = (reason as { stack?: unknown })?.stack;
    return typeof stack === 'string' && stack.trim() ? stack : null;
  } catch {
    return null;
  }
}

// ── report-once bookkeeping ──────────────────────────────────────────────────
//
// An error is reported by exactly one layer. The API client owns every error it
// produces — including the ones it deliberately stays quiet about (aborts, a
// GET 401) — and stamps them here, so a UI-level reporter can tell "nobody has
// logged this" from "the transport already did".
//
// A WeakSet rather than a property on the error: a frozen or cross-realm error
// must not throw on assignment, and nothing should appear in a serialized copy.
const reported = new WeakSet<object>();

export function markReported<E>(error: E): E {
  if (error !== null && typeof error === 'object') reported.add(error as object);
  return error;
}

export function wasReported(error: unknown): boolean {
  return error !== null && typeof error === 'object' && reported.has(error as object);
}

// ── trace ids ────────────────────────────────────────────────────────────────
export function newTraceId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ── breadcrumbs ──────────────────────────────────────────────────────────────
export function addBreadcrumb(kind: string, msg: string): void {
  breadcrumbs.push({ t: new Date().toISOString(), kind, msg: redact(msg).slice(0, 200) });
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
}

// ── capture ──────────────────────────────────────────────────────────────────
interface CaptureInput {
  level?: Level;
  message: string;
  error_kind?: string;
  stack?: string | null;
  route?: string | null;
  http_status?: number | null;
  trace_id?: string | null;
  /** Extra structured detail. Merged alongside breadcrumbs into context_json,
   *  which the Logs screen already renders as arbitrary key/value rows. Prefer
   *  it over interpolating into `message`: the storage layer templatizes the
   *  message to group incidents, so an inlined count splits the group. (A bare
   *  count also used to come back as [redacted-num]; the digit rule is
   *  token-aware now, but the grouping reason stands on its own.) */
  context?: Record<string, unknown>;
}

export function captureError(input: CaptureInput): void {
  const line: LogLine = {
    ts: new Date().toISOString(),
    level: input.level ?? 'error',
    source: 'frontend',
    message: redact(input.message).slice(0, 4000),
    error_kind: input.error_kind ?? null,
    stack_stub: input.stack ? redact(input.stack).slice(0, 4000) : null,
    route: input.route ? redact(input.route) : (typeof location !== 'undefined' ? location.pathname : null),
    http_status: input.http_status ?? null,
    trace_id: input.trace_id ?? null,
    // Recent breadcrumbs give the "how did we get here" trail for debugging.
    context_json: JSON.stringify({
      ...(input.context ?? {}),
      ...(droppedLines ? { telemetry_dropped: droppedLines } : {}),
      breadcrumbs: breadcrumbs.slice(-MAX_BREADCRUMBS),
      ua: navUA(),
      // The server overwrites `ts` with its own clock — rightly, a browser clock
      // cannot be trusted for ordering — so the moment the user actually saw the
      // failure would otherwise be lost. Batches flush up to 4s later, and a
      // beacon on `pagehide` can be much later than that.
      client_ts: new Date().toISOString(),
    }).slice(0, 8000),
    // `__BUILD_SHA__` is the build id vite actually defines (from VITE_BUILD_SHA,
    // see vite.config.ts). This used to read `VITE_RELEASE`, which is set
    // nowhere in the repo — so it was always null, and the server then filled the
    // gap with the retired Worker's version id. Every frontend line was stamped with
    // another component's release, which points a bisect at the wrong deploy.
    release: typeof __BUILD_SHA__ === 'string' ? __BUILD_SHA__ : null,
    env: (import.meta.env.MODE as string) ?? null,
  };
  enqueue(line);
  scheduleFlush(ERROR_FLUSH_DELAY_MS);
}

/** Called by the API client when a request fails, with the request's trace_id. */
export function captureApiError(o: {
  method: string;
  path: string;
  status: number | null;
  trace_id: string;
  message: string;
  /** The envelope's `error.code`, when the response carried one. */
  code?: string;
  /** Overrides the default `api_error`. The client sets this for network-level
   *  failures, where the engine's message says nothing usable and the kind is
   *  the only thing separating an outage from one dropped connection. */
  error_kind?: string;
  /** Probe evidence behind `error_kind`, so an operator can overrule it. */
  context?: Record<string, unknown>;
}): void {
  // A 401 on a read is an expected idle-session redirect. A 401 on a mutation
  // is different: a long multipart upload can outlive its session, and dropping
  // it silently is exactly how an upload disappears with no trace anywhere.
  if (o.status === 401) {
    if (o.method === 'GET' || o.method === 'HEAD') return;
    captureError({
      level: 'warn',
      error_kind: 'session_expired',
      message: `session_expired: ${o.method} ${o.path} → 401 (session ended mid-operation)`,
      route: `${o.method} ${o.path}`,
      http_status: 401,
      trace_id: o.trace_id,
    });
    return;
  }
  // A 503 `unavailable` is the server saying its database stalled and it
  // could not answer — the API already logged that at error level with
  // the statement's name. Echoing it as a second error from here doubled every
  // stall into two "new error" alerts per route (2026-08-26). It stays visible,
  // as a warn, under its own kind. A 503 `updating` is its sibling from the
  // revision-keyed cache: a domain behind the projection moved and the server
  // is rebuilding it, which it also reports itself — same policy, under a kind
  // an operator can filter apart from a stall. A 503 for any other reason (a
  // capability not configured) is still ours to report.
  const transientKind = o.status === 503 && o.code === 'unavailable'
    ? 'api_unavailable'
    : o.status === 503 && o.code === 'updating'
      ? 'api_updating'
      : null;
  captureError({
    level: transientKind === null && o.status && o.status >= 500 ? 'error' : 'warn',
    error_kind: o.error_kind ?? transientKind ?? 'api_error',
    message: `${o.method} ${o.path} → ${o.status ?? 'network'}: ${o.message}`,
    route: `${o.method} ${o.path}`,
    http_status: o.status,
    trace_id: o.trace_id,
    context: o.context,
  });
}

function navUA(): string {
  try {
    return typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 160) : '';
  } catch {
    return '';
  }
}

let deliveryFailures = 0;
let droppedLines = 0;
const MAX_DELIVERY_RETRIES = 2;
// The CSRF cookie the endpoint last answered 401 under: that session is over,
// so nothing posts again until a login mints a different one.
let refusedSession: string | null = null;

/**
 * The session cookie to post under, or null. `POST /telemetry` is
 * session-only, and the `csrf` cookie is the one session fact a page can read:
 * login sets it beside the HttpOnly `sid` with the same lifetime, logout clears
 * both. Without it there is no session to post under
 * (a guest on /creative-review or /login), and posting anyway only turns every
 * designed public error into a 401 and two retries. Lines stay buffered,
 * bounded by MAX_QUEUE, and `flush` keeps looking for a session every
 * SESSION_POLL_MS, so they go out soon after a login without waiting for
 * another line to be captured.
 */
function sessionToPostUnder(): string | null {
  let csrf: string | null;
  try {
    csrf = readCsrf();
  } catch {
    return null; // no readable cookie jar: nothing to post under
  }
  return csrf && csrf !== refusedSession ? csrf : null;
}

/** Put lines back at the head of the queue, oldest first, counting any the bound trims. */
function requeue(lines: LogLine[]): void {
  queue.unshift(...lines);
  while (queue.length > MAX_QUEUE) {
    queue.shift();
    droppedLines += 1;
  }
}

// ── queue + flush ────────────────────────────────────────────────────────────
function enqueue(line: LogLine): void {
  queue.push(line);
  if (queue.length > MAX_QUEUE) queue.shift();
}

function scheduleFlush(delay = FLUSH_DELAY_MS): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush(false);
  }, delay);
}

function flush(useBeacon: boolean): void {
  if (!queue.length) return;
  const session = sessionToPostUnder();
  if (session === null) {
    scheduleFlush(SESSION_POLL_MS);
    return;
  }
  const lines = queue.splice(0, queue.length);
  const body = JSON.stringify({ lines });

  // A failed delivery used to be swallowed and the lines discarded. That is the
  // same class of bug this module exists to catch, one layer up, and worse: if
  // the endpoint starts failing the Logs screen goes quiet, and quiet reads as
  // healthy. Put the batch back on failure, bounded so a permanently-rejecting
  // endpoint cannot spin, and report how many lines were lost.
  const onFailure = () => {
    deliveryFailures += 1;
    if (deliveryFailures <= MAX_DELIVERY_RETRIES) {
      requeue(lines);
      scheduleFlush(FLUSH_DELAY_MS);
    } else {
      droppedLines += lines.length;
    }
  };
  // A 401 is not a delivery failure to retry: the session ended. Keep the
  // lines (a `session_expired` line is exactly what a re-login should still
  // deliver) and post nothing more under this cookie.
  const onUnauthorized = () => {
    refusedSession = session;
    requeue(lines);
    scheduleFlush(SESSION_POLL_MS);
  };
  const onSuccess = () => {
    deliveryFailures = 0;
  };

  try {
    // text/plain keeps it a "simple" CORS request (no preflight); cookies ride
    // along so the session-authed endpoint accepts it.
    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      // sendBeacon returns false when the payload is refused outright; it
      // cannot report a later HTTP failure, and there is no retry after
      // pagehide anyway.
      if (!navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))) {
        droppedLines += lines.length;
      }
    } else {
      void fetch(ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain' },
        body,
      })
        .then((res) => (res.ok ? onSuccess() : res.status === 401 ? onUnauthorized() : onFailure()))
        .catch(onFailure);
    }
  } catch {
    onFailure();
  }
}

/** Lines this browser gave up on. Attached to the next line that does get
 *  through, so a partially-broken pipe is visible inside the Logs screen
 *  itself rather than only as an absence. */
export function telemetryDropped(): number {
  return droppedLines;
}

// ── init (idempotent) ────────────────────────────────────────────────────────
export function initTelemetry(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  window.addEventListener('error', (e: ErrorEvent) => {
    captureError({
      error_kind: 'window_error',
      message: e.message || describeError(e.error, 'Uncaught error'),
      stack: stackOf(e.error) ?? `${e.filename}:${e.lineno}:${e.colno}`,
      context: { operation: 'window_error', error_kind: typeTag(e.error) },
    });
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason: unknown = e.reason;
    const tag = typeTag(reason);
    // Duck-typed rather than importing ApiError: this module is standalone by
    // design, and a rejection can carry anything. What matters is that a
    // rejected ApiError arrives correlated instead of anonymous.
    const traceId = (reason as { traceId?: unknown; trace_id?: unknown })?.traceId
      ?? (reason as { trace_id?: unknown })?.trace_id;
    const status = (reason as { status?: unknown })?.status;
    captureError({
      error_kind: 'unhandled_rejection',
      message: describeError(reason, `Unhandled promise rejection (${tag})`),
      stack: stackOf(reason),
      trace_id: typeof traceId === 'string' ? traceId : null,
      http_status: typeof status === 'number' ? status : null,
      context: { operation: 'unhandled_rejection', error_kind: tag },
    });
  });

  // Flush pending events when the tab is hidden/unloaded (beacon survives unload).
  const flushOnHide = () => flush(true);
  window.addEventListener('pagehide', flushOnHide);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOnHide();
  });
}
